// src/app/AddBlog.jsx
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { BlogService } from "../api/blog.api";
import { validateBlogForm, formatBlogDate } from "../utils/blogUtils";
import { designTokens } from "../theme/designTokens";
import {
    Save,
    X,
    Upload,
    Globe,
    Eye,
    EyeOff,
    Plus,
    Trash2,
    AlertCircle,
    CheckCircle,
    Loader,
    Image as ImageIcon,
    Edit3
} from 'lucide-react';

const styles = {
    pageContainer: {
        maxWidth: "1000px",
        margin: "0 auto",
        padding: "2rem",
        background: designTokens.colors.background.primary,
        minHeight: "100vh",
        position: "relative",
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "2rem",
        flexWrap: "wrap",
        gap: "1rem",
    },
    title: {
        fontFamily: designTokens.typography.fontFamilies.display,
        fontSize: "2.5rem",
        fontWeight: 300,
        color: designTokens.colors.text.primary,
        margin: 0,
    },
    subtitle: {
        color: designTokens.colors.text.secondary,
        marginTop: "0.5rem",
        fontSize: "1rem",
    },
    headerActions: {
        display: "flex",
        gap: "1rem",
    },
    previewButton: {
        padding: "0.75rem 1.5rem",
        background: "transparent",
        border: `1px solid ${designTokens.colors.border.light}`,
        borderRadius: "0.5rem",
        color: designTokens.colors.text.secondary,
        fontSize: "0.875rem",
        fontWeight: 500,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        transition: "all 0.2s ease",
    },
    draftButton: {
        padding: "0.75rem 1.5rem",
        background: designTokens.colors.background.secondary,
        border: `1px solid ${designTokens.colors.border.light}`,
        borderRadius: "0.5rem",
        color: designTokens.colors.text.primary,
        fontSize: "0.875rem",
        fontWeight: 500,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
    },
    publishButton: {
        padding: "0.75rem 2rem",
        background: designTokens.colors.champagne,
        color: "white",
        border: "none",
        borderRadius: "0.5rem",
        fontSize: "0.875rem",
        fontWeight: 500,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        transition: "all 0.2s ease",
    },
    successMessage: {
        padding: "1rem",
        background: "#4CAF50",
        color: "white",
        borderRadius: "0.5rem",
        marginBottom: "1rem",
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
    },
    errorMessage: {
        padding: "1rem",
        background: "#f44336",
        color: "white",
        borderRadius: "0.5rem",
        marginBottom: "1rem",
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
    },
    seoScore: {
        marginBottom: "2rem",
        padding: "1rem",
        background: designTokens.colors.background.secondary,
        borderRadius: "0.5rem",
    },
    seoScoreHeader: {
        display: "flex",
        justifyContent: "space-between",
        marginBottom: "0.5rem",
        color: designTokens.colors.text.secondary,
    },
    progressBar: {
        width: "100%",
        height: "8px",
        background: designTokens.colors.border.light,
        borderRadius: "4px",
        overflow: "hidden",
    },
    tabs: {
        display: "flex",
        gap: "1rem",
        marginBottom: "2rem",
        borderBottom: `1px solid ${designTokens.colors.border.light}`,
    },
    tab: {
        padding: "0.75rem 1.5rem",
        background: "transparent",
        border: "none",
        fontSize: "1rem",
        fontWeight: 500,
        cursor: "pointer",
        transition: "all 0.2s ease",
    },
    form: {
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
    },
    fieldGroup: {
        marginBottom: "1.5rem",
    },
    label: {
        display: "block",
        fontSize: "0.875rem",
        fontWeight: 500,
        color: designTokens.colors.text.primary,
        marginBottom: "0.5rem",
    },
    required: {
        color: "#f44336",
    },
    input: {
        width: "100%",
        padding: "0.75rem",
        background: designTokens.colors.background.primary,
        border: `1px solid ${designTokens.colors.border.light}`,
        borderRadius: "0.5rem",
        fontSize: "1rem",
        color: designTokens.colors.text.primary,
        transition: "all 0.2s ease",
    },
    textarea: {
        width: "100%",
        padding: "0.75rem",
        background: designTokens.colors.background.primary,
        border: `1px solid ${designTokens.colors.border.light}`,
        borderRadius: "0.5rem",
        fontSize: "1rem",
        color: designTokens.colors.text.primary,
        transition: "all 0.2s ease",
        resize: "vertical",
        minHeight: "100px",
    },
    fieldHint: {
        fontSize: "0.75rem",
        color: designTokens.colors.text.tertiary,
        marginTop: "0.25rem",
        display: "flex",
        justifyContent: "space-between",
    },
    generateButton: {
        padding: "0.75rem 1.5rem",
        background: designTokens.colors.background.secondary,
        border: `1px solid ${designTokens.colors.border.light}`,
        borderRadius: "0.5rem",
        color: designTokens.colors.text.secondary,
        fontSize: "0.875rem",
        cursor: "pointer",
        whiteSpace: "nowrap",
    },
    generateInlineButton: {
        position: "absolute",
        right: "0.5rem",
        top: "50%",
        transform: "translateY(-50%)",
        padding: "0.25rem 0.75rem",
        background: designTokens.colors.champagne,
        color: "white",
        border: "none",
        borderRadius: "0.25rem",
        fontSize: "0.75rem",
        cursor: "pointer",
    },
    uploadArea: {
        border: `2px dashed ${designTokens.colors.border.light}`,
        borderRadius: "0.5rem",
        padding: "2rem",
        textAlign: "center",
        background: designTokens.colors.background.secondary,
        cursor: "pointer",
        transition: "all 0.2s ease",
        color: designTokens.colors.text.secondary,
    },
    imagePreview: {
        position: "relative",
        borderRadius: "0.5rem",
        overflow: "hidden",
        maxWidth: "300px",
    },
    removeImageButton: {
        position: "absolute",
        top: "0.5rem",
        right: "0.5rem",
        padding: "0.5rem",
        background: "rgba(0,0,0,0.5)",
        border: "none",
        borderRadius: "50%",
        color: "white",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    multipleImageGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
        gap: "1rem",
        marginTop: "1rem",
    },
    imageCard: {
        position: "relative",
        border: `1px solid ${designTokens.colors.border.light}`,
        borderRadius: "0.5rem",
        padding: "0.5rem",
        background: designTokens.colors.background.secondary,
    },
    imageCardImage: {
        width: "100%",
        height: "120px",
        objectFit: "cover",
        borderRadius: "0.5rem",
        marginBottom: "0.5rem",
    },
    removeCardButton: {
        position: "absolute",
        top: "0.75rem",
        right: "0.75rem",
        background: "#f44336",
        color: "white",
        border: "none",
        borderRadius: "50%",
        width: "24px",
        height: "24px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "16px",
        fontWeight: "bold",
    },
    tagsContainer: {
        display: "flex",
        flexWrap: "wrap",
        gap: "0.5rem",
        marginTop: "1rem",
    },
    tag: {
        padding: "0.375rem 0.875rem",
        background: `${designTokens.colors.champagne}15`,
        color: designTokens.colors.champagne,
        borderRadius: "9999px",
        fontSize: "0.875rem",
        display: "inline-flex",
        alignItems: "center",
    },
    tagWithRemove: {
        padding: "0.375rem 0.875rem",
        background: `${designTokens.colors.champagne}15`,
        color: designTokens.colors.champagne,
        borderRadius: "9999px",
        fontSize: "0.875rem",
        display: "inline-flex",
        alignItems: "center",
        gap: "0.5rem",
        paddingRight: "0.5rem",
    },
    removeTagButton: {
        background: "transparent",
        border: "none",
        color: "inherit",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        padding: "2px",
    },
    suggestedTags: {
        display: "flex",
        flexWrap: "wrap",
        gap: "0.5rem",
        marginTop: "0.5rem",
    },
    suggestedTag: {
        padding: "0.25rem 0.75rem",
        background: "transparent",
        border: `1px solid ${designTokens.colors.border.light}`,
        borderRadius: "9999px",
        color: designTokens.colors.text.secondary,
        fontSize: "0.75rem",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: "0.25rem",
    },
    toggleContainer: {
        display: "flex",
        gap: "1rem",
        padding: "0.25rem",
        background: designTokens.colors.background.secondary,
        borderRadius: "0.5rem",
    },
    toggleButton: {
        flex: 1,
        padding: "0.75rem",
        border: "none",
        borderRadius: "0.5rem",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.5rem",
        transition: "all 0.2s ease",
    },
    infoBox: {
        marginTop: "2rem",
        padding: "1.5rem",
        background: designTokens.colors.background.secondary,
        borderRadius: "0.5rem",
    },
    infoBoxTitle: {
        fontSize: "1rem",
        fontWeight: 600,
        color: designTokens.colors.text.primary,
        marginBottom: "1rem",
    },
    infoRow: {
        display: "flex",
        justifyContent: "space-between",
        padding: "0.5rem 0",
        borderBottom: `1px solid ${designTokens.colors.border.light}`,
        color: designTokens.colors.text.secondary,
    },
    loadingContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        gap: "1rem",
        color: designTokens.colors.text.secondary,
    },
    previewTitle: {
        fontSize: "1.5rem",
        fontWeight: 300,
        marginBottom: "1.5rem",
        color: designTokens.colors.text.primary,
    },
    previewH1: {
        fontFamily: designTokens.typography.fontFamilies.display,
        fontSize: "2rem",
        fontWeight: 300,
        marginBottom: "1rem",
        color: designTokens.colors.text.primary,
    },
    previewContent: {
        fontSize: "1rem",
        lineHeight: 1.6,
        color: designTokens.colors.text.secondary,
    },
    errorText: {
        color: "#f44336",
        fontSize: "0.875rem",
        marginTop: "0.25rem",
        display: "flex",
        alignItems: "center",
        gap: "0.25rem",
    },
    multipleUploadArea: {
        border: `2px dashed ${designTokens.colors.border.light}`,
        borderRadius: "0.5rem",
        padding: "1.5rem",
        textAlign: "center",
        background: designTokens.colors.background.secondary,
        cursor: "pointer",
        transition: "all 0.2s ease",
        color: designTokens.colors.text.secondary,
        marginTop: "1rem",
    },
    editModeBadge: {
        display: "inline-flex",
        alignItems: "center",
        gap: "0.5rem",
        padding: "0.25rem 1rem",
        background: designTokens.colors.champagne,
        color: "white",
        borderRadius: "9999px",
        fontSize: "0.875rem",
        marginLeft: "1rem",
    },
};

// Error Message Component
const ErrorMessage = ({ message }) => (
    <div className="error-message" style={styles.errorText}>
        <AlertCircle size={14} />
        {message}
    </div>
);

export default function AddBlog() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = !!id;

    // Form state
    const [form, setForm] = useState({
        title: "",
        slug: "",
        metaTitle: "",
        metaDescription: "",
        content: "",
        excerpt: "",
        category: "",
        tags: [],
        focusKeyword: "",
        secondaryKeywords: [],
        status: "draft",
        isIndexed: true,
        canonicalUrl: "",
        coverImage: {
            url: "",
            alt: "",
            title: ""
        },
        images: [] // Multiple images array
    });

    // Original data for comparison
    const [originalData, setOriginalData] = useState(null);

    // UI state
    const [tagInput, setTagInput] = useState("");
    const [keywordInput, setKeywordInput] = useState("");
    const [uploading, setUploading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [successMessage, setSuccessMessage] = useState("");
    const [showPreview, setShowPreview] = useState(false);
    const [categories, setCategories] = useState([]);
    const [suggestedTags, setSuggestedTags] = useState([]);
    const [seoScore, setSeoScore] = useState(0);
    const [activeTab, setActiveTab] = useState("content");
    const [dataLoaded, setDataLoaded] = useState(false);

    // Available categories
    const availableCategories = [
        "skincare",
        "makeup",
        "haircare",
        "wellness",
        "lifestyle",
        "science",
        "sustainability",
        "ingredients",
        "tutorials",
        "reviews"
    ];

    // Load blog data if in edit mode
    useEffect(() => {
        if (isEditMode && id) {
            loadBlogForEdit();
        } else {
            setDataLoaded(true);
        }
        loadCategories();
        loadSuggestedTags();
    }, [id]);

    // Calculate SEO score when relevant fields change
    useEffect(() => {
        if (dataLoaded) {
            calculateSeoScore();
        }
    }, [form.title, form.metaTitle, form.metaDescription, form.focusKeyword, form.content, form.excerpt, form.coverImage, form.tags, dataLoaded]);

    const loadBlogForEdit = async () => {
        setLoading(true);
        try {
            console.log("Loading blog for edit with ID/slug:", id);
            const blog = await BlogService.getBlogBySlug(id);
            console.log("Loaded blog data:", blog);

            // Store original data
            setOriginalData(blog);

            // Prepare form data
            const formData = {
                title: blog.title || "",
                slug: blog.slug || "",
                metaTitle: blog.metaTitle || "",
                metaDescription: blog.metaDescription || "",
                content: blog.content || "",
                excerpt: blog.excerpt || "",
                category: blog.category || "",
                tags: blog.tags || [],
                focusKeyword: blog.focusKeyword || "",
                secondaryKeywords: blog.secondaryKeywords || [],
                status: blog.status || "draft",
                isIndexed: blog.isIndexed !== false,
                canonicalUrl: blog.canonicalUrl || "",
                coverImage: blog.coverImage || { url: "", alt: "", title: "" },
                images: blog.images || []
            };

            console.log("Setting form data:", formData);
            setForm(formData);

            // Set tag and keyword inputs
            setTagInput(blog.tags?.join(", ") || "");
            setKeywordInput(blog.secondaryKeywords?.join(", ") || "");

            setDataLoaded(true);

        } catch (error) {
            console.error("Failed to load blog:", error);
            setErrors({ submit: "Failed to load blog for editing: " + error.message });
            setDataLoaded(true);
        } finally {
            setLoading(false);
        }
    };

    const loadCategories = async () => {
        try {
            const data = await BlogService.getBlogCategories();
            if (data.success && data.data) {
                setCategories(data.data);
            }
        } catch (error) {
            console.error("Failed to load categories:", error);
        }
    };

    const loadSuggestedTags = async () => {
        try {
            const data = await BlogService.getTagsCloud();
            if (data.success && data.data) {
                setSuggestedTags(data.data.slice(0, 10).map(tag => tag._id));
            }
        } catch (error) {
            console.error("Failed to load suggested tags:", error);
        }
    };

    const calculateSeoScore = () => {
        let score = 0;
        const checks = {
            title: form.title.length >= 30 && form.title.length <= 60,
            metaTitle: form.metaTitle.length >= 30 && form.metaTitle.length <= 60,
            metaDescription: form.metaDescription.length >= 120 && form.metaDescription.length <= 160,
            excerpt: form.excerpt.length >= 50 && form.excerpt.length <= 160,
            focusKeyword: form.focusKeyword.length > 0,
            content: form.content.split(/\s+/).length >= 300,
            slug: form.slug.length > 0 && !form.slug.includes(' '),
            coverImage: form.coverImage.url && form.coverImage.alt,
            tags: form.tags.length >= 3,
        };

        const totalChecks = Object.keys(checks).length;
        const passedChecks = Object.values(checks).filter(Boolean).length;
        score = Math.round((passedChecks / totalChecks) * 100);

        setSeoScore(score);
    };

    // Handle text inputs
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (type === "checkbox") {
            setForm({ ...form, [name]: checked });
            return;
        }

        if (name === "title") {
            const slug = value
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/(^-|-$)+/g, "");

            setForm(prev => ({
                ...prev,
                title: value,
                slug: prev.slug === prev.title?.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)+/g, "")
                    ? slug
                    : prev.slug
            }));
        }
        else if (name.startsWith("coverImage.")) {
            const key = name.split(".")[1];
            setForm({
                ...form,
                coverImage: { ...form.coverImage, [key]: value }
            });
        }
        else if (name.startsWith("image.")) {
            // Handle individual image field updates
            const parts = name.split(".");
            if (parts.length === 3) {
                const index = parseInt(parts[1]);
                const field = parts[2];
                const updatedImages = [...form.images];
                if (updatedImages[index]) {
                    updatedImages[index][field] = value;
                    setForm({ ...form, images: updatedImages });
                }
            }
        }
        else {
            setForm({ ...form, [name]: value });
        }

        // Mark field as touched
        setTouched(prev => ({ ...prev, [name]: true }));
    };

    const handleBlur = (e) => {
        const { name } = e.target;
        setTouched(prev => ({ ...prev, [name]: true }));
    };

    // Handle tags
    const handleTagInputChange = (e) => {
        setTagInput(e.target.value);
        const tags = e.target.value.split(",").map(tag => tag.trim()).filter(Boolean);
        setForm(prev => ({ ...prev, tags }));
    };

    const addSuggestedTag = (tag) => {
        if (!form.tags.includes(tag)) {
            const newTags = [...form.tags, tag];
            setForm(prev => ({ ...prev, tags: newTags }));
            setTagInput(newTags.join(", "));
        }
    };

    const removeTag = (tagToRemove) => {
        const newTags = form.tags.filter(tag => tag !== tagToRemove);
        setForm(prev => ({ ...prev, tags: newTags }));
        setTagInput(newTags.join(", "));
    };

    // Handle secondary keywords
    const handleKeywordChange = (e) => {
        setKeywordInput(e.target.value);
        const keywords = e.target.value.split(",").map(kw => kw.trim()).filter(Boolean);
        setForm(prev => ({ ...prev, secondaryKeywords: keywords }));
    };

    // Cover image upload
    const uploadCoverImage = async (file) => {
        setUploading(true);
        setErrors(prev => ({ ...prev, coverImage: null }));

        try {
            const imageUrl = await BlogService.uploadImage(file);
            setForm({
                ...form,
                coverImage: { ...form.coverImage, url: imageUrl }
            });
            setSuccessMessage("Cover image uploaded successfully!");
            setTimeout(() => setSuccessMessage(""), 3000);
        } catch (error) {
            console.error("Upload failed:", error);
            setErrors(prev => ({ ...prev, coverImage: error.message }));
        } finally {
            setUploading(false);
        }
    };

    const handleCoverImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            setErrors(prev => ({ ...prev, coverImage: "Please upload an image file" }));
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            setErrors(prev => ({ ...prev, coverImage: "Image size should be less than 10MB" }));
            return;
        }

        await uploadCoverImage(file);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        const file = e.dataTransfer.files[0];
        if (file && file.type.startsWith('image/')) {
            await uploadCoverImage(file);
        }
    };

    const removeCoverImage = () => {
        setForm({
            ...form,
            coverImage: { url: "", alt: "", title: "" }
        });
    };

    // Multiple images upload
    const uploadMultipleImages = async (files) => {
        setUploading(true);
        setErrors(prev => ({ ...prev, images: null }));

        try {
            const uploadedImages = [];

            for (let file of files) {
                if (!file.type.startsWith("image/")) continue;

                const imageUrl = await BlogService.uploadImage(file);

                uploadedImages.push({
                    url: imageUrl,
                    alt: "",
                    title: ""
                });
            }

            setForm(prev => ({
                ...prev,
                images: [...prev.images, ...uploadedImages]
            }));

            setSuccessMessage(`${uploadedImages.length} images uploaded successfully!`);
            setTimeout(() => setSuccessMessage(""), 3000);

        } catch (error) {
            console.error("Multiple upload failed:", error);
            setErrors(prev => ({ ...prev, images: error.message }));
        } finally {
            setUploading(false);
        }
    };

    const handleMultipleImageUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;

        await uploadMultipleImages(files);
    };

    const removeImage = (index) => {
        const updatedImages = [...form.images];
        updatedImages.splice(index, 1);
        setForm(prev => ({ ...prev, images: updatedImages }));
    };

    const updateImageField = (index, field, value) => {
        const updatedImages = [...form.images];
        if (updatedImages[index]) {
            updatedImages[index][field] = value;
            setForm(prev => ({ ...prev, images: updatedImages }));
        }
    };

    // Form validation
    const validateForm = () => {
        const validationErrors = validateBlogForm(form);
        setErrors(validationErrors);
        return Object.keys(validationErrors).length === 0;
    };

    // Generate slug from title
    const generateSlug = () => {
        const slug = form.title
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/(^-|-$)+/g, "");
        setForm({ ...form, slug });
    };

    // Auto-generate meta title from title
    const generateMetaTitle = () => {
        if (!form.metaTitle && form.title) {
            setForm({ ...form, metaTitle: form.title + " | KROMAIC Blog" });
        }
    };

    // Auto-generate excerpt from content
    const generateExcerpt = () => {
        if (!form.excerpt && form.content) {
            const plainText = form.content.replace(/<[^>]*>/g, '');
            const excerpt = plainText.substring(0, 150) + "...";
            setForm({ ...form, excerpt });
        }
    };

    // Submit blog
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            const firstError = document.querySelector('.error-message');
            firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }

        setSubmitting(true);
        setErrors({});

        try {
            const payload = {
                ...form,
                tags: form.tags.filter(Boolean),
                secondaryKeywords: form.secondaryKeywords.filter(Boolean),
                images: form.images, // Include multiple images
                canonicalUrl: form.canonicalUrl?.trim() || undefined,
                publishedAt: form.status === 'published' && !isEditMode ? new Date().toISOString() : form.publishedAt,
            };

            console.log("Submitting payload:", payload);

            if (isEditMode) {
                await BlogService.updateBlog(id, payload);
                setSuccessMessage("Blog updated successfully!");
            } else {
                await BlogService.createBlog(payload);
                setSuccessMessage("Blog created successfully!");
            }

            setTimeout(() => {
                navigate("/admin/blogs");
            }, 1500);

        } catch (error) {
            console.error("Submission error:", error);
            setErrors({ submit: error.message });
        } finally {
            setSubmitting(false);
        }
    };

    // Save as draft
    const handleSaveDraft = async () => {
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                status: 'draft',
                tags: form.tags.filter(Boolean),
                secondaryKeywords: form.secondaryKeywords.filter(Boolean),
                images: form.images,
            };

            if (isEditMode) {
                await BlogService.updateBlog(id, payload);
                setSuccessMessage("Draft updated successfully!");
            } else {
                await BlogService.createBlog(payload);
                setSuccessMessage("Draft saved successfully!");
            }

            setTimeout(() => setSuccessMessage(""), 3000);

        } catch (error) {
            setErrors({ submit: error.message });
        } finally {
            setSubmitting(false);
        }
    };

    // Preview mode
    const PreviewModal = () => (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.8)',
                zIndex: 1000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '2rem',
            }}
            onClick={() => setShowPreview(false)}
        >
            <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                style={{
                    background: designTokens.colors.background.primary,
                    borderRadius: '1rem',
                    maxWidth: '800px',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    padding: '2rem',
                    position: 'relative',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={() => setShowPreview(false)}
                    style={{
                        position: 'absolute',
                        top: '1rem',
                        right: '1rem',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        color: designTokens.colors.text.secondary,
                    }}
                >
                    <X size={24} />
                </button>

                <h2 style={styles.previewTitle}>Blog Preview</h2>

                {form.coverImage.url && (
                    <div style={{ marginBottom: '1.5rem' }}>
                        <img
                            src={form.coverImage.url}
                            alt={form.coverImage.alt || form.title}
                            style={{
                                width: '100%',
                                maxHeight: '300px',
                                objectFit: 'cover',
                                borderRadius: '0.5rem',
                            }}
                        />
                        {form.coverImage.alt && (
                            <p style={{ fontSize: '0.875rem', color: designTokens.colors.text.tertiary, marginTop: '0.5rem' }}>
                                Alt: {form.coverImage.alt}
                            </p>
                        )}
                    </div>
                )}

                <h1 style={styles.previewH1}>{form.title || "Untitled"}</h1>

                <div style={{
                    display: 'flex',
                    gap: '1rem',
                    marginBottom: '1rem',
                    color: designTokens.colors.text.secondary,
                    fontSize: '0.875rem',
                    flexWrap: 'wrap',
                }}>
                    {form.category && (
                        <span style={{
                            padding: '0.25rem 0.75rem',
                            background: `${designTokens.colors.champagne}15`,
                            borderRadius: '9999px',
                        }}>
                            Category: {form.category}
                        </span>
                    )}
                    {form.tags.length > 0 && (
                        <span>Tags: {form.tags.join(', ')}</span>
                    )}
                </div>

                {form.excerpt && (
                    <p style={{
                        fontStyle: 'italic',
                        color: designTokens.colors.text.secondary,
                        borderLeft: `3px solid ${designTokens.colors.champagne}`,
                        paddingLeft: '1rem',
                        marginBottom: '2rem',
                    }}>
                        {form.excerpt}
                    </p>
                )}

                {/* Preview multiple images */}
                {form.images.length > 0 && (
                    <div style={{ marginBottom: '2rem' }}>
                        <h3 style={{ fontSize: '1.125rem', marginBottom: '1rem' }}>Additional Images</h3>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                            gap: '1rem',
                        }}>
                            {form.images.map((img, index) => (
                                <div key={index}>
                                    <img
                                        src={img.url}
                                        alt={img.alt || ''}
                                        style={{
                                            width: '100%',
                                            height: '100px',
                                            objectFit: 'cover',
                                            borderRadius: '0.5rem',
                                        }}
                                    />
                                    {img.alt && (
                                        <p style={{ fontSize: '0.75rem', color: designTokens.colors.text.tertiary, marginTop: '0.25rem' }}>
                                            {img.alt}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div
                    dangerouslySetInnerHTML={{ __html: form.content || "<p>No content yet...</p>" }}
                    style={styles.previewContent}
                />

                {form.focusKeyword && (
                    <div style={{ marginTop: '2rem', padding: '1rem', background: designTokens.colors.background.secondary, borderRadius: '0.5rem' }}>
                        <p><strong>Focus Keyword:</strong> {form.focusKeyword}</p>
                        {form.secondaryKeywords.length > 0 && (
                            <p><strong>Secondary Keywords:</strong> {form.secondaryKeywords.join(', ')}</p>
                        )}
                    </div>
                )}
            </motion.div>
        </motion.div>
    );

    // Add global keyframe animations
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            @keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
            }
            .spin {
                animation: spin 1s linear infinite;
            }
            input.error, textarea.error, select.error {
                border-color: #f44336 !important;
            }
        `;
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    if (loading) {
        return (
            <div style={styles.loadingContainer}>
                <Loader size={40} className="spin" />
                <p>Loading blog data...</p>
            </div>
        );
    }

    return (
        <div style={styles.pageContainer}>
            {/* Header */}
            <div style={styles.header}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <h1 style={styles.title}>
                        {isEditMode ? "Edit Blog Post" : "Create New Blog Post"}
                    </h1>
                    {isEditMode && (
                        <span style={styles.editModeBadge}>
                            <Edit3 size={14} />
                            Editing: {form.slug}
                        </span>
                    )}
                </div>
                <div style={styles.headerActions}>
                    <button
                        onClick={() => setShowPreview(true)}
                        style={styles.previewButton}
                        disabled={submitting}
                    >
                        <Eye size={18} />
                        Preview
                    </button>
                    <button
                        onClick={handleSaveDraft}
                        style={styles.draftButton}
                        disabled={submitting}
                    >
                        <Save size={18} />
                        {isEditMode ? "Update Draft" : "Save Draft"}
                    </button>
                    <button
                        onClick={handleSubmit}
                        style={styles.publishButton}
                        disabled={submitting}
                    >
                        {submitting ? (
                            <>
                                <Loader size={18} className="spin" />
                                {isEditMode ? "Updating..." : "Publishing..."}
                            </>
                        ) : (
                            <>
                                <CheckCircle size={18} />
                                {isEditMode ? "Update & Publish" : "Publish"}
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Success Message */}
            <AnimatePresence>
                {successMessage && (
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        style={styles.successMessage}
                    >
                        <CheckCircle size={20} />
                        {successMessage}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Error Message */}
            {errors.submit && (
                <div style={styles.errorMessage}>
                    <AlertCircle size={20} />
                    {errors.submit}
                </div>
            )}

            {/* SEO Score */}
            <div style={styles.seoScore}>
                <div style={styles.seoScoreHeader}>
                    <span>SEO Optimization Score</span>
                    <span style={{
                        color: seoScore >= 80 ? '#4CAF50' : seoScore >= 50 ? '#FF9800' : '#f44336',
                        fontWeight: 'bold',
                    }}>
                        {seoScore}%
                    </span>
                </div>
                <div style={styles.progressBar}>
                    <div style={{
                        width: `${seoScore}%`,
                        height: '100%',
                        background: seoScore >= 80 ? '#4CAF50' : seoScore >= 50 ? '#FF9800' : '#f44336',
                        borderRadius: 'inherit',
                        transition: 'width 0.3s ease',
                    }} />
                </div>
            </div>

            {/* Tabs */}
            <div style={styles.tabs}>
                <button
                    onClick={() => setActiveTab("content")}
                    style={{
                        ...styles.tab,
                        borderBottom: activeTab === "content" ? `2px solid ${designTokens.colors.champagne}` : 'none',
                        color: activeTab === "content" ? designTokens.colors.champagne : designTokens.colors.text.secondary,
                    }}
                >
                    Content
                </button>
                <button
                    onClick={() => setActiveTab("seo")}
                    style={{
                        ...styles.tab,
                        borderBottom: activeTab === "seo" ? `2px solid ${designTokens.colors.champagne}` : 'none',
                        color: activeTab === "seo" ? designTokens.colors.champagne : designTokens.colors.text.secondary,
                    }}
                >
                    SEO & Metadata
                </button>
                <button
                    onClick={() => setActiveTab("settings")}
                    style={{
                        ...styles.tab,
                        borderBottom: activeTab === "settings" ? `2px solid ${designTokens.colors.champagne}` : 'none',
                        color: activeTab === "settings" ? designTokens.colors.champagne : designTokens.colors.text.secondary,
                    }}
                >
                    Settings
                </button>
            </div>

            {/* Main Form */}
            <form onSubmit={handleSubmit} style={styles.form}>
                {/* Content Tab */}
                {activeTab === "content" && (
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                    >
                        {/* Title */}
                        <div style={styles.fieldGroup}>
                            <label style={styles.label}>
                                Title <span style={styles.required}>*</span>
                            </label>
                            <input
                                name="title"
                                value={form.title}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="e.g., 5 Anti-Aging Ingredients That Actually Work"
                                style={styles.input}
                                className={touched.title && errors.title ? "error" : ""}
                            />
                            {touched.title && errors.title && (
                                <ErrorMessage message={errors.title} />
                            )}
                            <div style={styles.fieldHint}>
                                <span>Characters: {form.title.length}/60</span>
                                <span style={{
                                    color: form.title.length > 60 ? '#f44336' : 'inherit'
                                }}>
                                    {form.title.length > 60 ? ' (too long)' : ''}
                                </span>
                            </div>
                        </div>

                        {/* Slug */}
                        <div style={styles.fieldGroup}>
                            <label style={styles.label}>
                                Slug <span style={styles.required}>*</span>
                            </label>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <input
                                    name="slug"
                                    value={form.slug}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder="e.g., 5-anti-aging-ingredients"
                                    style={{ ...styles.input, flex: 1 }}
                                    className={touched.slug && errors.slug ? "error" : ""}
                                />
                                <button
                                    type="button"
                                    onClick={generateSlug}
                                    style={styles.generateButton}
                                    title="Generate from title"
                                >
                                    Generate
                                </button>
                            </div>
                            {touched.slug && errors.slug && (
                                <ErrorMessage message={errors.slug} />
                            )}
                            <div style={styles.fieldHint}>
                                URL-friendly version of the title
                            </div>
                        </div>

                        {/* Category */}
                        <div style={styles.fieldGroup}>
                            <label style={styles.label}>
                                Category <span style={styles.required}>*</span>
                            </label>
                            <select
                                name="category"
                                value={form.category}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                style={styles.input}
                                className={touched.category && errors.category ? "error" : ""}
                            >
                                <option value="">Select a category</option>
                                {availableCategories.map(cat => (
                                    <option key={cat} value={cat}>
                                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                                    </option>
                                ))}
                            </select>
                            {touched.category && errors.category && (
                                <ErrorMessage message={errors.category} />
                            )}
                        </div>

                        {/* Excerpt */}
                        <div style={styles.fieldGroup}>
                            <label style={styles.label}>
                                Excerpt <span style={styles.required}>*</span>
                            </label>
                            <div style={{ position: 'relative' }}>
                                <textarea
                                    name="excerpt"
                                    value={form.excerpt}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder="Brief summary of the blog post (150-160 characters recommended)"
                                    rows={3}
                                    style={{ ...styles.textarea, paddingRight: '80px' }}
                                    className={touched.excerpt && errors.excerpt ? "error" : ""}
                                />
                                {!form.excerpt && form.content && (
                                    <button
                                        type="button"
                                        onClick={generateExcerpt}
                                        style={styles.generateInlineButton}
                                    >
                                        Generate
                                    </button>
                                )}
                            </div>
                            {touched.excerpt && errors.excerpt && (
                                <ErrorMessage message={errors.excerpt} />
                            )}
                            <div style={styles.fieldHint}>
                                <span>Characters: {form.excerpt.length}/160</span>
                                <span style={{
                                    color: form.excerpt.length > 160 ? '#f44336' : 'inherit'
                                }}>
                                    {form.excerpt.length > 160 ? ' (too long)' : ''}
                                </span>
                            </div>
                        </div>

                        {/* Cover Image */}
                        <div style={styles.fieldGroup}>
                            <label style={styles.label}>
                                Cover Image <span style={styles.required}>*</span>
                            </label>

                            {!form.coverImage.url ? (
                                <div
                                    onDragOver={handleDragOver}
                                    onDrop={handleDrop}
                                    style={styles.uploadArea}
                                    onClick={() => document.getElementById("coverImageInput").click()}
                                >
                                    <input
                                        id="coverImageInput"
                                        type="file"
                                        accept="image/*"
                                        onChange={handleCoverImageUpload}
                                        style={{ display: "none" }}
                                    />

                                    {uploading ? (
                                        <>
                                            <Loader size={40} className="spin" />
                                            <p>Uploading...</p>
                                        </>
                                    ) : (
                                        <>
                                            <Upload size={40} color={designTokens.colors.champagne} />
                                            <p>Click to upload or drag and drop</p>
                                            <small>PNG, JPG, GIF up to 10MB</small>
                                        </>
                                    )}
                                </div>
                            ) : (
                                <div style={styles.imagePreview}>
                                    <img
                                        src={form.coverImage.url}
                                        alt={form.coverImage.alt || "Cover preview"}
                                        style={{ width: '100%', height: 'auto', display: 'block' }}
                                    />
                                    <button
                                        type="button"
                                        onClick={removeCoverImage}
                                        style={styles.removeImageButton}
                                        title="Remove image"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            )}

                            {errors.coverImage && (
                                <ErrorMessage message={errors.coverImage} />
                            )}

                            {/* Image Alt Text */}
                            {form.coverImage.url && (
                                <>
                                    <input
                                        name="coverImage.alt"
                                        placeholder="Alt text for image (required for accessibility)"
                                        value={form.coverImage.alt}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        style={{ ...styles.input, marginTop: '1rem' }}
                                        className={touched['coverImage.alt'] && errors.coverImageAlt ? "error" : ""}
                                    />
                                    {touched['coverImage.alt'] && errors.coverImageAlt && (
                                        <ErrorMessage message={errors.coverImageAlt} />
                                    )}

                                    <input
                                        name="coverImage.title"
                                        placeholder="Image title (optional)"
                                        value={form.coverImage.title}
                                        onChange={handleChange}
                                        style={{ ...styles.input, marginTop: '0.5rem' }}
                                    />
                                </>
                            )}
                        </div>

                        {/* Multiple Images Section */}
                        <div style={styles.fieldGroup}>
                            <label style={styles.label}>
                                Additional Blog Images
                                <span style={{ ...styles.fieldHint, marginLeft: '0.5rem' }}>
                                    (Upload multiple images for your blog)
                                </span>
                            </label>

                            {/* Upload Area */}
                            <div
                                style={styles.multipleUploadArea}
                                onClick={() => document.getElementById("multipleImageInput").click()}
                            >
                                <input
                                    id="multipleImageInput"
                                    type="file"
                                    multiple
                                    accept="image/*"
                                    onChange={handleMultipleImageUpload}
                                    style={{ display: "none" }}
                                />
                                <ImageIcon size={30} color={designTokens.colors.champagne} />
                                <p>Click to upload multiple images</p>
                                <small>You can select multiple files at once</small>
                            </div>

                            {errors.images && <ErrorMessage message={errors.images} />}

                            {/* Images Grid */}
                            {form.images.length > 0 && (
                                <div style={styles.multipleImageGrid}>
                                    {form.images.map((img, index) => (
                                        <div key={index} style={styles.imageCard}>
                                            <img
                                                src={img.url}
                                                alt={img.alt || ''}
                                                style={styles.imageCardImage}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => removeImage(index)}
                                                style={styles.removeCardButton}
                                                title="Remove image"
                                            >
                                                ×
                                            </button>

                                            <input
                                                name={`image.${index}.alt`}
                                                placeholder="Alt text"
                                                value={img.alt}
                                                onChange={(e) => updateImageField(index, 'alt', e.target.value)}
                                                style={{ ...styles.input, marginBottom: '0.5rem', fontSize: '0.875rem' }}
                                            />

                                            <input
                                                name={`image.${index}.title`}
                                                placeholder="Title"
                                                value={img.title}
                                                onChange={(e) => updateImageField(index, 'title', e.target.value)}
                                                style={{ ...styles.input, fontSize: '0.875rem' }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Content */}
                        <div style={styles.fieldGroup}>
                            <label style={styles.label}>
                                Content <span style={styles.required}>*</span>
                            </label>
                            <textarea
                                name="content"
                                value={form.content}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Write your blog content here. HTML is allowed."
                                rows={15}
                                style={{ ...styles.textarea, fontFamily: "monospace" }}
                                className={touched.content && errors.content ? "error" : ""}
                            />
                            {touched.content && errors.content && (
                                <ErrorMessage message={errors.content} />
                            )}
                            <div style={styles.fieldHint}>
                                <span>Words: {form.content.split(/\s+/).filter(Boolean).length}</span>
                                <span>Characters: {form.content.length}</span>
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* SEO Tab */}
                {activeTab === "seo" && (
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                    >
                        {/* Focus Keyword */}
                        <div style={styles.fieldGroup}>
                            <label style={styles.label}>
                                Focus Keyword <span style={styles.required}>*</span>
                            </label>
                            <input
                                name="focusKeyword"
                                value={form.focusKeyword}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="e.g., anti aging ingredients"
                                style={styles.input}
                                className={touched.focusKeyword && errors.focusKeyword ? "error" : ""}
                            />
                            {touched.focusKeyword && errors.focusKeyword && (
                                <ErrorMessage message={errors.focusKeyword} />
                            )}
                            <div style={styles.fieldHint}>
                                Primary keyword you want to rank for
                            </div>
                        </div>

                        {/* Meta Title */}
                        <div style={styles.fieldGroup}>
                            <label style={styles.label}>
                                Meta Title <span style={styles.required}>*</span>
                            </label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    name="metaTitle"
                                    value={form.metaTitle}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder="SEO title (50-60 characters recommended)"
                                    style={styles.input}
                                    className={touched.metaTitle && errors.metaTitle ? "error" : ""}
                                />
                                {!form.metaTitle && form.title && (
                                    <button
                                        type="button"
                                        onClick={generateMetaTitle}
                                        style={styles.generateInlineButton}
                                    >
                                        Generate
                                    </button>
                                )}
                            </div>
                            {touched.metaTitle && errors.metaTitle && (
                                <ErrorMessage message={errors.metaTitle} />
                            )}
                            <div style={styles.fieldHint}>
                                <span>Characters: {form.metaTitle.length}/60</span>
                                <span style={{
                                    color: form.metaTitle.length > 60 ? '#f44336' : 'inherit'
                                }}>
                                    {form.metaTitle.length > 60 ? ' (too long)' : ''}
                                </span>
                            </div>
                        </div>

                        {/* Meta Description */}
                        <div style={styles.fieldGroup}>
                            <label style={styles.label}>
                                Meta Description <span style={styles.required}>*</span>
                            </label>
                            <textarea
                                name="metaDescription"
                                value={form.metaDescription}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="SEO description (150-160 characters recommended)"
                                rows={3}
                                style={styles.textarea}
                                className={touched.metaDescription && errors.metaDescription ? "error" : ""}
                            />
                            {touched.metaDescription && errors.metaDescription && (
                                <ErrorMessage message={errors.metaDescription} />
                            )}
                            <div style={styles.fieldHint}>
                                <span>Characters: {form.metaDescription.length}/160</span>
                                <span style={{
                                    color: form.metaDescription.length > 160 ? '#f44336' : 'inherit'
                                }}>
                                    {form.metaDescription.length > 160 ? ' (too long)' : ''}
                                </span>
                            </div>
                        </div>

                        {/* Secondary Keywords */}
                        <div style={styles.fieldGroup}>
                            <label style={styles.label}>
                                Secondary Keywords
                            </label>
                            <input
                                name="secondaryKeywords"
                                value={keywordInput}
                                onChange={handleKeywordChange}
                                placeholder="e.g., wrinkle reduction, collagen production"
                                style={styles.input}
                            />
                            <div style={styles.fieldHint}>
                                Enter keywords separated by commas
                            </div>

                            {form.secondaryKeywords.length > 0 && (
                                <div style={styles.tagsContainer}>
                                    {form.secondaryKeywords.map((keyword, index) => (
                                        <span key={index} style={styles.tag}>
                                            {keyword}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Tags */}
                        <div style={styles.fieldGroup}>
                            <label style={styles.label}>
                                Tags <span style={styles.required}>*</span>
                            </label>
                            <input
                                name="tags"
                                value={tagInput}
                                onChange={handleTagInputChange}
                                onBlur={handleBlur}
                                placeholder="e.g., anti-aging, skincare, retinol"
                                style={styles.input}
                                className={touched.tags && errors.tags ? "error" : ""}
                            />
                            {touched.tags && errors.tags && (
                                <ErrorMessage message={errors.tags} />
                            )}
                            <div style={styles.fieldHint}>
                                Enter tags separated by commas
                            </div>

                            {form.tags.length > 0 && (
                                <div style={styles.tagsContainer}>
                                    {form.tags.map((tag, index) => (
                                        <span key={index} style={styles.tagWithRemove}>
                                            #{tag}
                                            <button
                                                type="button"
                                                onClick={() => removeTag(tag)}
                                                style={styles.removeTagButton}
                                            >
                                                <X size={12} />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}

                            {suggestedTags.length > 0 && (
                                <div style={{ marginTop: '1rem' }}>
                                    <small style={{ color: designTokens.colors.text.tertiary }}>
                                        Suggested tags:
                                    </small>
                                    <div style={styles.suggestedTags}>
                                        {suggestedTags.map(tag => (
                                            !form.tags.includes(tag) && (
                                                <button
                                                    key={tag}
                                                    type="button"
                                                    onClick={() => addSuggestedTag(tag)}
                                                    style={styles.suggestedTag}
                                                >
                                                    <Plus size={12} />
                                                    #{tag}
                                                </button>
                                            )
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}

                {/* Settings Tab */}
                {activeTab === "settings" && (
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                    >
                        {/* Status */}
                        <div style={styles.fieldGroup}>
                            <label style={styles.label}>Publication Status</label>
                            <div style={styles.toggleContainer}>
                                <button
                                    type="button"
                                    onClick={() => setForm({ ...form, status: "draft" })}
                                    style={{
                                        ...styles.toggleButton,
                                        background: form.status === "draft" ? designTokens.colors.champagne : 'transparent',
                                        color: form.status === "draft" ? 'white' : designTokens.colors.text.secondary,
                                    }}
                                >
                                    <EyeOff size={16} />
                                    Draft
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setForm({ ...form, status: "published" })}
                                    style={{
                                        ...styles.toggleButton,
                                        background: form.status === "published" ? designTokens.colors.champagne : 'transparent',
                                        color: form.status === "published" ? 'white' : designTokens.colors.text.secondary,
                                    }}
                                >
                                    <Eye size={16} />
                                    Published
                                </button>
                            </div>
                        </div>

                        {/* Indexing */}
                        <div style={styles.fieldGroup}>
                            <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <input
                                    type="checkbox"
                                    name="isIndexed"
                                    checked={form.isIndexed}
                                    onChange={handleChange}
                                />
                                Allow search engines to index this page
                            </label>
                            <div style={styles.fieldHint}>
                                Uncheck to prevent this page from appearing in search results (noindex)
                            </div>
                        </div>

                        {/* Canonical URL */}
                        <div style={styles.fieldGroup}>
                            <label style={styles.label}>
                                Canonical URL
                                <Globe size={14} style={{ marginLeft: '0.5rem' }} />
                            </label>
                            <input
                                name="canonicalUrl"
                                value={form.canonicalUrl}
                                onChange={handleChange}
                                placeholder="https://kromaic.com/blog/your-post"
                                style={styles.input}
                            />
                            <div style={styles.fieldHint}>
                                Leave empty to use the default blog URL
                            </div>
                        </div>

                        {/* Blog Info */}
                        <div style={styles.infoBox}>
                            <h4 style={styles.infoBoxTitle}>Blog Information</h4>
                            <div style={styles.infoRow}>
                                <span>Status:</span>
                                <span style={{
                                    color: form.status === 'published' ? '#4CAF50' : '#FF9800',
                                    fontWeight: 500,
                                }}>
                                    {form.status === 'published' ? 'Published' : 'Draft'}
                                </span>
                            </div>
                            {isEditMode && originalData && (
                                <>
                                    <div style={styles.infoRow}>
                                        <span>Created:</span>
                                        <span>{formatBlogDate(originalData.createdAt) || 'Not available'}</span>
                                    </div>
                                    <div style={styles.infoRow}>
                                        <span>Last updated:</span>
                                        <span>{formatBlogDate(originalData.updatedAt) || 'Not available'}</span>
                                    </div>
                                    <div style={styles.infoRow}>
                                        <span>Views:</span>
                                        <span>{originalData.views || 0}</span>
                                    </div>
                                </>
                            )}
                            <div style={styles.infoRow}>
                                <span>Word count:</span>
                                <span>{form.content.split(/\s+/).filter(Boolean).length}</span>
                            </div>
                            <div style={styles.infoRow}>
                                <span>Tags count:</span>
                                <span>{form.tags.length}</span>
                            </div>
                            <div style={styles.infoRow}>
                                <span>Images count:</span>
                                <span>{form.images.length}</span>
                            </div>
                        </div>
                    </motion.div>
                )}
            </form>

            {/* Preview Modal */}
            <AnimatePresence>
                {showPreview && <PreviewModal />}
            </AnimatePresence>
        </div>
    );
}